import type { WireRenderer } from "../wire-renderer";
import { ChartingColorScheme, type WireDiscoveryComponent } from "@glide/fluent-components/dist/js/fluent-components";
import { useWireDiscovery, WireDiscoveryProvider } from "./wire-discovery-provider";
import { DiscoverySearchBox } from "./wire-discover-search-box";
import { WireDataPlotChartWithColorization } from "../wire-data-plot/wire-data-plot";
import React from "react";
import { DataPlotType, UIButtonAppearance, UISize } from "@glide/wire";
import { NewTable } from "../../components/new-table/new-table";
import { getAgentClientForAppID } from "./agent-client";
import { Spinner } from "../wire-spinner/wire-spinner";
import * as Table from "../../components/new-table/new-table-lib";
import { assertNever } from "@glideapps/ts-necessities";
import isEmpty from "lodash/isEmpty";
import { AppIcon } from "@glide/common";
import { WireButton } from "../wire-button/wire-button";
import { useAppEnvironment } from "@glide/common-core/dist/js/use-app-environment";
import { MarkdownView } from "../../components/markdown/markdown-view";
export const FunLoader = ({ value }: { value: string }) => {
    if (value.indexOf("have fun") > -1) {
        return (
            <div tw="flex items-center justify-center mt-8">
                <img
                    src="https://media.tenor.com/jtS4RMiJDXUAAAAi/clippy-windows-xp.gif"
                    alt="Doing stuffs."
                    tw="w-[75px]"
                />
            </div>
        );
    } else {
        // Question why do we need a backend for a spinner.. ?
        // A: we don't but it's a wire component type
        // Refactor back to dumb components coming soon...
        return <Spinner size={UISize.Large} tw="mt-4" backend={{} as any} />;
    }
};

export const WireDiscoveryWithProvider: WireRenderer<WireDiscoveryComponent> = p => {
    const appEnvironment = useAppEnvironment(p.backend.appID);
    const authenticator = appEnvironment?.authenticator;
    const agent = React.useMemo(
        () => getAgentClientForAppID(p.backend.appID, undefined, authenticator?.appUserID),
        [authenticator?.appUserID, p.backend.appID]
    );

    return (
        <WireDiscoveryProvider agentServiceClientCreator={agent}>
            <WireDiscovery {...p} />
        </WireDiscoveryProvider>
    );
};

function chartTypeToIconPath(chartType: DataPlotType) {
    switch (chartType) {
        case DataPlotType.Bar:
            return "path:/svg/monotone/mt-pages-bar-chart.svg";
        case DataPlotType.Line:
            return "path:/svg/monotone/mt-pages-line-chart.svg";
        case DataPlotType.Area:
            return "path:/svg/monotone/mt-pages-area-chart.svg";
        case DataPlotType.Scatter:
            return "path:/svg/monotone/mt-pages-scatter-plot.svg";
        default:
            assertNever(chartType);
    }
}

function responseKeyToTabLabel(key: string): string {
    switch (key) {
        case "summary":
            return "Summary";
        case "insights":
            return "Insights";
        case "assumptions":
            return "Assumptions";
        case "sql":
            return "SQL";
        default:
            return "";
    }
}

export const WireDiscovery: WireRenderer<WireDiscoveryComponent> = _ => {
    const useDiscovery = useWireDiscovery();
    const { onSearchInputChange, onSubmitNewPrompt, searchInputValue } = useDiscovery;

    return (
        <div tw="w-full h-full flex flex-col">
            <div tw="w-full flex items-center">
                <DiscoverySearchBox
                    value={searchInputValue}
                    onChange={e => onSearchInputChange(e.target.value)}
                    onSubmit={() => onSubmitNewPrompt()}
                />
            </div>
            <DiscoveryContent />
        </div>
    );
};

const DiscoveryContent = () => {
    const useDiscovery = useWireDiscovery();
    const {
        searchInputValue,
        isAgentRequestInFlight,
        charts = [],
        table,
        isResponseError,
        llmResponseMessage,
        llmStructuredResponse,
        singleDataPointView,
    } = useDiscovery;

    const [visibleChartIndex, _setVisibleChartIndex] = React.useState<number>(0);
    const [chartTypeVisual, setChartTypeVisual] = React.useState<DataPlotType>(DataPlotType.Bar);
    const [selectedResponseTab, setSelectedResponseTab] = React.useState<string>("summary");

    const { summary, assumptions, sql, insights, trends } = llmStructuredResponse ?? {};

    const structuredTabsToDisplay = React.useMemo(
        () =>
            ["summary", "insights", "assumptions", "sql"].filter(
                k => !isEmpty(llmStructuredResponse?.[k as keyof typeof llmStructuredResponse])
            ),
        [llmStructuredResponse]
    );

    const contentToDisplay = React.useMemo(() => {
        switch (selectedResponseTab) {
            case "summary":
                return summary;
            case "insights":
                return insights;
            case "assumptions":
                return assumptions;
            case "sql":
                return sql;
            default:
                return null;
        }
    }, [selectedResponseTab, insights, assumptions, summary, sql]);

    const chartsWithTypeMapping = charts.map(chart => {
        const columnsWithType = chart.columnsToDisplay.map(column => ({
            ...column,
            type: chartTypeVisual,
        }));
        return {
            ...chart,
            columnsToDisplay: columnsWithType,
        };
    });

    if (isAgentRequestInFlight) {
        return (
            <div tw="w-full h-full flex items-center justify-center">
                <FunLoader value={searchInputValue} />
            </div>
        );
    }

    if (isResponseError) {
        return (
            <div tw="w-full h-full flex flex-col items-center justify-center pt-7 text-text-contextual-base ">
                <p tw="text-text-contextual-base ">There was an error processing this request.</p>
                <WireButton
                    tw="mt-4 cursor-pointer"
                    iconName="path:/svg/stroke/st-arrow-sync.svg"
                    appearance={UIButtonAppearance.Simple}
                    onClick={() => {
                        useDiscovery.onSubmitNewPrompt();
                    }}>
                    Try again
                </WireButton>
            </div>
        );
    }
    return (
        <>
            {!isEmpty(llmStructuredResponse) ? (
                <div tw="w-full my-4 border-b border-border-base">
                    <div tw="w-full flex flex-row items-center justify-items-stretch space-x-1">
                        {structuredTabsToDisplay.map((tab, index) => (
                            <button
                                key={index}
                                className={selectedResponseTab === tab ? "selected" : ""}
                                tw="px-8 py-1 text-sm rounded-lg bg-transparent text-text-pale font-bold grow focus:bg-n100 [&.selected]:(bg-n300A text-text-contextual-base)"
                                onClick={() => setSelectedResponseTab(tab)}>
                                {responseKeyToTabLabel(tab)}
                            </button>
                        ))}
                    </div>
                    <div tw="px-2 py-4 text-text-contextual-base text-sm">
                        <MarkdownView disableStyleInjection={true} markdown={contentToDisplay ?? ""} />
                    </div>
                </div>
            ) : null}
            {trends && trends.length > 0 ? (
                <div tw="my-2 flex flex-row items-center justify-between px-3">
                    {trends.map((trend, index) => (
                        <div key={index} tw="flex flex-col items-start justify-start">
                            <span tw="text-2xl font-extrabold text-text-contextual-base">{trend.value}</span>
                            <span tw="text-text-xpale text-xs">{trend.label}</span>
                        </div>
                    ))}
                </div>
            ) : null}
            {singleDataPointView ? (
                <div tw="w-full p-8 bg-n100A text-center text-text-contextual-base mt-2 rounded-lg">
                    <h1 tw="text-7xl">{singleDataPointView.value}</h1>
                    <h2 tw="text-2xl mt-4">{singleDataPointView.label}</h2>
                </div>
            ) : null}
            {charts.length > 0 ? (
                <div tw="w-full">
                    <div tw="w-full flex items-center justify-between">
                        <h2 tw="self-stretch mt-4 font-semibold text-text-dark">
                            {charts[visibleChartIndex].graphTitle}
                        </h2>

                        <div tw="flex items-center justify-end space-x-1 p-2">
                            {[DataPlotType.Bar, DataPlotType.Line, DataPlotType.Area, DataPlotType.Scatter].map(
                                chartType => (
                                    <button
                                        key={chartType}
                                        className={chartType === chartTypeVisual ? "selected" : ""}
                                        tw="flex items-center justify-center h-8 w-8 rounded-md bg-n200A text-text-pale focus:bg-n100 [&.selected]:(bg-n300A text-text-contextual-base)"
                                        onClick={() => setChartTypeVisual(chartType)}>
                                        <AppIcon icon={chartTypeToIconPath(chartType)} tw="scale-75" />
                                    </button>
                                )
                            )}
                        </div>
                    </div>

                    <div tw="mt-3">
                        {/* <h3>{charts[visibleChartIndex].label}</h3> */}
                        <WireDataPlotChartWithColorization
                            colorScheme={ChartingColorScheme.Default}
                            gradientColors={[]}
                            isLoading={false}
                            height={300}
                            {...chartsWithTypeMapping[visibleChartIndex]}
                        />
                    </div>
                </div>
            ) : null}
            {table !== undefined ? (
                <div tw="w-full">
                    <div tw="mt-3">
                        <Table.Table>
                            <NewTable {...table} tw="w-full" />
                        </Table.Table>
                    </div>
                </div>
            ) : null}
            {isEmpty(llmStructuredResponse) && llmResponseMessage !== undefined ? (
                <div tw="w-full my-2 p-2 text-text-contextual-base mt-4">{llmResponseMessage}</div>
            ) : null}
        </>
    );
};
